import React, { useState, useMemo } from 'react';

const HeightWeightBmiStep = ({ question, value, onAnswer, onNext, onDisqualify, isOnTreatment = false }) => {
  const [feet, setFeet] = useState(value?.feet || '');
  const [inches, setInches] = useState(value?.inches || '');
  const [weight, setWeight] = useState(value?.weight || '');
  const [error, setError] = useState('');

  // Check if BMI check should be skipped (e.g., for Sermorelin)
  const skipBmiCheck = question?.skipBmiCheck === true;

  // BMI thresholds based on treatment status
  const bmiThreshold = isOnTreatment ? 20 : 25;

  // Calculate BMI using useMemo instead of useEffect.
  // BMI is ALWAYS computed for the payload (Beluga uses it for clinician
  // context). skipBmiCheck only suppresses the visible BMI card and the
  // disqualification gate below — the value still rides on the answer.
  const { bmi, bmiCategory } = useMemo(() => {
    if (feet && inches !== '' && weight) {
      const totalInches = (parseInt(feet) * 12) + parseInt(inches || 0);
      const weightLbs = parseFloat(weight);
      
      if (totalInches > 0 && weightLbs > 0) {
        // BMI formula: (weight in lbs / (height in inches)^2) * 703
        const calculatedBmi = (weightLbs / (totalInches * totalInches)) * 703;
        const bmiValue = calculatedBmi.toFixed(1);
        
        // Determine BMI category
        let category = '';
        if (calculatedBmi < 18.5) {
          category = 'Underweight';
        } else if (calculatedBmi < 25) {
          category = 'Normal weight';
        } else if (calculatedBmi < 30) {
          category = 'Overweight';
        } else if (calculatedBmi < 35) {
          category = 'Obesity Class I';
        } else if (calculatedBmi < 40) {
          category = 'Obesity Class II';
        } else {
          category = 'Obesity Class III';
        }
        
        return { bmi: bmiValue, bmiCategory: category };
      }
    }
    return { bmi: null, bmiCategory: '' };
  }, [feet, inches, weight]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!feet || !weight) {
      setError('Please fill in all required fields');
      return;
    }

    if (parseInt(feet) < 3 || parseInt(feet) > 8) {
      setError('Please enter a valid height');
      return;
    }

    if (parseFloat(weight) < 50 || parseFloat(weight) > 800) {
      setError('Please enter a valid weight');
      return;
    }

    // If BMI check is skipped (e.g., Sermorelin), still include the BMI in
    // the answer so Beluga's payload carries it — just bypass the
    // disqualification gate below.
    if (skipBmiCheck) {
      onAnswer({
        feet,
        inches: inches || '0',
        weight,
        bmi,
        bmiCategory
      });
      onNext();
      return;
    }

    const calculatedBmi = parseFloat(bmi);
    
    // Check BMI disqualification
    if (calculatedBmi < bmiThreshold) {
      const reason = isOnTreatment 
        ? `Based on your BMI calculation (${bmi}), which is below 20, you may not be a candidate for continued GLP-1 treatment. Please consult with your healthcare provider.`
        : `Based on your BMI calculation (${bmi}), which is below 25, you may not meet the criteria for GLP-1 weight loss treatment. This medication is typically prescribed for individuals with a BMI of 25 or higher. Please consult with your healthcare provider for other options.`;
      
      onAnswer({ 
        feet, 
        inches: inches || '0', 
        weight, 
        bmi,
        bmiCategory 
      });
      
      if (onDisqualify) {
        onDisqualify(reason);
      }
      return;
    }

    onAnswer({ 
      feet, 
      inches: inches || '0', 
      weight, 
      bmi,
      bmiCategory 
    });
    onNext();
  };

  // Get color based on BMI status
  const getBmiColor = () => {
    if (!bmi) return 'text-gray-500';
    const bmiValue = parseFloat(bmi);
    
    if (bmiValue < bmiThreshold) {
      return 'text-orange-600'; // Warning - may not qualify
    } else if (bmiValue < 30) {
      return 'text-[#2d8a5f]'; // Good - qualifies
    } else {
      return 'text-[#2d8a5f]'; // Also qualifies
    }
  };

  const getBmiBgColor = () => {
    if (!bmi) return 'bg-gray-50';
    const bmiValue = parseFloat(bmi);
    
    if (bmiValue < bmiThreshold) {
      return 'bg-orange-50 border border-orange-200';
    }
    return 'bg-[#f0faf4] border border-[#2d8a5f]/20';
  };

  return (
    <div data-testid={`step-${question.id}`}>
      {/* Question */}
      <h2 className="text-2xl md:text-3xl font-semibold text-center text-gray-900 mb-2">
        {question.question}
      </h2>

      {/* Subtitle */}
      {question.subtitle && (
        <p className="text-center text-gray-500 mb-6">
          {question.subtitle}
        </p>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Height */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Height <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-3">
            <div className="flex-1">
              <div className="relative">
                <input
                  type="number"
                  value={feet}
                  onChange={(e) => { setFeet(e.target.value); setError(''); }}
                  placeholder="5"
                  className="form-input text-center pr-12"
                  min="3"
                  max="8"
                  data-testid="feet-input"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">ft</span>
              </div>
            </div>
            <div className="flex-1">
              <div className="relative">
                <input
                  type="number"
                  value={inches}
                  onChange={(e) => { setInches(e.target.value); setError(''); }}
                  placeholder="8"
                  className="form-input text-center pr-12"
                  min="0"
                  max="11"
                  data-testid="inches-input"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">in</span>
              </div>
            </div>
          </div>
        </div>

        {/* Weight */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Weight <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type="number"
              value={weight}
              onChange={(e) => { setWeight(e.target.value); setError(''); }}
              placeholder="180"
              className="form-input text-center pr-12"
              min="50"
              max="800"
              data-testid="weight-input"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">lbs</span>
          </div>
        </div>

        {/* BMI Display - only show when BMI check is enabled */}
        {!skipBmiCheck && bmi && (
          <div className={`rounded-xl p-4 text-center ${getBmiBgColor()}`} data-testid="bmi-display">
            <p className="text-sm text-gray-500 mb-1">Your calculated BMI</p>
            <p className={`text-3xl font-bold ${getBmiColor()}`}>{bmi}</p>
            <p className={`text-sm font-medium mt-1 ${getBmiColor()}`}>
              {bmiCategory}
            </p>
            
            {/* Warning for low BMI */}
            {parseFloat(bmi) < bmiThreshold && (
              <div className="mt-3 text-sm text-orange-700 bg-orange-100 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span>
                    Your BMI is below {bmiThreshold}. GLP-1 medication is typically prescribed for patients with a BMI of {bmiThreshold} or higher.
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {error && (
          <p className="text-center text-red-500 text-sm" data-testid="error-message">
            {error}
          </p>
        )}

        <button
          type="submit"
          className="btn-primary"
          data-testid="continue-button"
        >
          Continue
        </button>
      </form>

      {/* BMI Info - only show when BMI check is enabled */}
      {!skipBmiCheck && (
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-400">
            BMI is calculated using the formula: (weight in lbs / height in inches²) × 703
          </p>
        </div>
      )}
    </div>
  );
};

export default HeightWeightBmiStep;
